import { Box, Flex, Text, Tooltip } from '@chakra-ui/react'
import React from 'react'
import { CLIENT_CONFIG } from '../../core/client/buildConfig'

interface SidebarItemProps {
  icon?: React.ReactElement | null
  label: string
  onClick?: () => void
  isSelected?: boolean
  // History keeps the active conversation highlighted after the pointer leaves.
  highlightSelection?: boolean
  // Only assistant/library shortcuts may use a selected icon tile.
  highlightIcon?: boolean
  showLabel?: boolean
  badge?: string | number
  // When true, the label is treated as sensitive: rendered with
  // `data-dd-privacy="mask"` so Datadog Session Replay redacts it, and the
  // hover Tooltip is suppressed (Chakra renders it in a portal that does not
  // inherit the privacy attribute).
  privacyMask?: boolean
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  onClick,
  isSelected = false,
  highlightSelection = false,
  highlightIcon = false,
  showLabel = true,
  badge,
  privacyMask = false,
}) => {
  const showSelection = isSelected && highlightSelection
  const badgeBgColor = CLIENT_CONFIG.theme.colors.secondary

  const content = (
    <Flex
      as='button'
      type='button'
      w='100%'
      textAlign='left'
      aria-current={isSelected ? 'page' : undefined}
      _focusVisible={{ outline: '2px solid', outlineColor: 'chat.focus', outlineOffset: '2px' }}
      minH={10}
      py={2}
      px={2}
      alignItems='center'
      borderRadius='chat.sm'
      cursor='pointer'
      onClick={onClick}
      transition='all 0.2s'
      justify={showLabel ? 'flex-start' : 'center'}
      position='relative'
      bg={showSelection ? 'chat.selected' : 'transparent'}
      sx={{
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': { bg: 'chat.selected', '& .sidebar-item-label': { color: 'chat.brand' } },
        },
      }}
    >
      {icon && (
        <Box
          w={5}
          h={5}
          bg={highlightIcon && isSelected ? 'chat.iconSurface' : undefined}
          borderRadius='chat.sm'
          mr={showLabel ? 3 : 0}
          fontSize='sm'
          color={showSelection || (highlightIcon && isSelected) ? 'chat.iconInk' : 'chat.muted'}
          display='flex'
          alignItems='center'
          justifyContent='center'
          flexShrink={0}
        >
          {icon}
        </Box>
      )}

      {showLabel && (
        <Text
          className='sidebar-item-label'
          flex='1'
          fontSize='md'
          lineHeight={6}
          minW={0}
          overflow='hidden'
          textOverflow='ellipsis'
          whiteSpace='nowrap'
          fontWeight={isSelected ? 'medium' : 'normal'}
          color={showSelection ? 'chat.brand' : 'inherit'}
          data-dd-privacy={privacyMask ? 'mask' : undefined}
        >
          {label}
        </Text>
      )}

      {badge && showLabel && (
        <Box
          fontSize='xs'
          px={2}
          py={0.5}
          borderRadius='full'
          bg={badgeBgColor}
          color='white'
          ml={2}
          lineHeight='normal'
        >
          {badge}
        </Box>
      )}
    </Flex>
  )

  if (privacyMask) {
    return content
  }

  return (
    <Tooltip label={label} placement='top' openDelay={500}>
      {content}
    </Tooltip>
  )
}

export default SidebarItem
